import '@fortawesome/fontawesome-free/css/all.css';
import {Button, DialogActions, DialogContent, DialogContentText, Typography, useMediaQuery} from '@material-ui/core';
import AgencyAdministrationRoutes from 'AgencyAdministration/Routes';
import AgentRoutes from 'Agent/Routes';
import AgentNewsAdministrationRoutes from 'AgentNewsAdministration/Routes';
import AuthorizedRoutes from 'AuthorizedRoutes';
import CoreRoutes from 'Core/Routes';
import Analytics from 'Core/components/Analytics/Analytics';
import PortalFooterWrapper from 'Core/components/Footer/PortalFooterWrapper';
import LegalNoticePage from 'Core/components/LegalNotices/LegalNoticePage';
import Login from 'Core/components/Login/Login';
import CollapsedMenuBar from 'Core/components/Navigation/CollapsedMenuBar';
import NavigationMenu from 'Core/components/Navigation/NavigationMenu';
import Notifications from 'Core/components/Notifications';
import LegalDocumentationRoutes from 'Core/components/TermsAndConditions/Routes';
import TermsAndConditions from 'Core/components/TermsAndConditions/TermsAndConditions';
import UserAccountNotFound from 'Core/components/UserAccountNotFound';
import WithTheme from 'Core/components/WithTheme';
import {FeatureConstants, getOrCreateFeatureFlag} from 'Core/ducks/featureFlag';
import {updatefeatureVariables} from 'Core/ducks/featureToggle';
import {ModalState, showModal} from 'Core/ducks/modal';
import {tokenRefresh} from 'Core/ducks/refreshToken';
import {fromResult, isLoaded} from 'Core/models/Async';
import {
  setExpiresAtForAutoRefresh,
  shouldRefreshToken,
  startWindowMessageListener,
} from 'Core/refreshTokenUtilityMethods';
import CustomerSupportRoutes from 'CustomerSupport/Routes';
import DocumentRoutes from 'Agent/DocumentUploadHistory/Routes';
import QuotingRoutes from 'Quoting/Routes';
import BreadcrumbsBar from 'Shared/components/BreadcrumbsBar';
import ErrorBoundary from 'Shared/components/ErrorBoundary';
import ModalConductor from 'Shared/components/ModalConductor';
import {isAuthenticated} from 'Shared/utilities/Auth';
import {getUserClaims} from 'Shared/utilities/UserProfileUtilities';
import {LocalStorageKey, fromLocalStorage} from 'Shared/utilities/localStorage';
import UserRoutes, {UserRouteConstants} from 'Users/Routes';
import IndependentAgentEntry from 'Users/components/AgentAdministration/AgencyAdministratorAgentEntry/IndependentAgentEntry';
import IndependentAgentRegistration from 'Users/components/IndependentAgentRegistration/IndependentAgentRegistration';
import IndependentAgentSignUp from 'Users/components/SignUp/ExternalUserSignUp';
import PassRecovery from 'Users/components/UserRecovery/PasswordReset/PasswordRecovery';
import PassRecoveryEmailSent from 'Users/components/UserRecovery/PasswordReset/PasswordRecoveryEmailSent';
import PassRecoverySecurityQues from 'Users/components/UserRecovery/PasswordReset/PasswordRecoverySecurityQues';
import PasswordReset from 'Users/components/UserRecovery/PasswordReset/PasswordReset';
import PasswordResetExpired from 'Users/components/UserRecovery/PasswordReset/PasswordResetExpired';
import UsernameRecovery from 'Users/components/UserRecovery/UsernameRecovery';
import WithClaims from 'WithClaims';
import {getFeatureToggleVariable} from 'feature';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {BrowserRouter, Route, Switch, useHistory, useLocation} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import './App.sass';
import {loadClaimsAndProfile} from './Core/components/Startup';
import StandardLogo from './Core/assets/images/StandardLogo.svg';
import { useMigrationRedirect } from 'Shared/components/MigrationRedirect/hook';
import Modal from 'Shared/components/Modal';

function loadAdobeAnalyticsHeader(
  environment: string,
  setAnalyticsScriptLoaded: React.Dispatch<React.SetStateAction<boolean>>
) {
  const script = document.createElement('script');
  script.src =
    environment === 'PROD'
      ? 'https://assets.adobedtm.com/d5ff4550c9ef/9a79ff9a9dee/launch-108539c8c5bf.min.js'
      : 'https://assets.adobedtm.com/d5ff4550c9ef/9a79ff9a9dee/launch-6efc7e4dead3-development.min.js';
  script.async = true;
  script.onload = () => {
    setAnalyticsScriptLoaded(true);
  };
  document.head.appendChild(script);
}

function loadSisenseScriptHeader(
  environment: string,
  setSisenseScriptLoaded: React.Dispatch<React.SetStateAction<boolean>>
) {
  const script = document.createElement('script');
  switch (environment) {
    case 'PROD':
      script.src = `https://aws0afiprodsis01.aws0.afib.cloud/js/sisense.js?wat=true`;
      break;
    case 'UAT':
      script.src = `https://aws0afitestsisense01.aws0.afib.cloud/js/sisense.js?wat=true`;
      break;
    case 'STAGING':
      script.src = `https://aws0afitestsisense02.aws0.afib.cloud/js/sisense.js?wat=true`;
      break;
    case 'PRE-PROD':
      script.src = `https://aws0afistagesis01.aws0.afib.cloud/js/sisense.js?wat=true`;
      break;
    case 'DEV':
    case 'DEV2':
    case 'DEV3':
    case 'AIRLOCK':
    case 'INT2':
    case 'LOCAL':
    case 'INT':
      script.src = `https://aws0afisis01.aws0.afib.cloud/js/sisense.js?wat=true`;
      break;
  }
  script.async = true;
  script.onload = () => {
    setSisenseScriptLoaded(true);
  };
  document.head.appendChild(script);
}

function loadWhatFixHeader() {
  const script = document.createElement('script');
  script.src = 'https://cdn.whatfix.com/prod/1b07e88b-fc7a-4fd6-bf0e-cf9b781515f6/initiator/initiator.nocache.js';
  script.async = true;
  document.head.appendChild(script);
}

export const App = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const isMobileView = !useMediaQuery((theme: any) => theme.breakpoints.up('lg'));

  const Menu = CollapsedMenuBar;
  const footerFeatureVariables = getFeatureToggleVariable();
  const {userClaims} = useSelector((state: ReducerState) => state.user.profile);
  const [analyticsScriptLoaded, setAnalyticsScriptLoaded] = useState(false);
  const [sisenseScriptLoaded, setSisenseScriptLoaded] = useState(false);
   const {secondsRemaining, isShowModal, toURL} = useMigrationRedirect({
    countdownMS: 15000, //user will redirect in 15 seconds
    mappings: [
      {
        match: /abbrokerportal-prod-support.allstate.com/i,
        targetBaseURL: 'https://myaccess-prod-support.standard.com',
      },
      {match: /abbrokerportal-preprod.allstate/i, targetBaseURL: 'https://myaccess-pre-prod.standard.com'},
      {match: /myaccess.allstatebenefits/i, targetBaseURL: 'https://myaccess.standard.com'},
      {match: /abbrokerportal.allstate.com/i, targetBaseURL: 'https://myaccess.standard.com'},
    ],
  });
  const urlText = useMemo(() => (toURL ? new URL(toURL).hostname.toString() : ''), [toURL]);
  const secondsText = secondsRemaining > 1 ? `${secondsRemaining} seconds` : `${secondsRemaining} second`;

  useEffect(() => {
    if (userClaims.key === 'Loaded' && isAuthenticated()) {
      dispatch(getOrCreateFeatureFlag(FeatureConstants.BR_FOOTER));
      dispatch(getOrCreateFeatureFlag(FeatureConstants.BR_SISENSE));
      dispatch(getOrCreateFeatureFlag(FeatureConstants.BR_WHATFIX));
      dispatch(getOrCreateFeatureFlag(FeatureConstants.BR_A360_EnrollmentUpload));
      dispatch(getOrCreateFeatureFlag(FeatureConstants.enable_portals_virus_scan));
      dispatch(getOrCreateFeatureFlag(FeatureConstants.ENABLE_TAX_PORT_YEAR_FILTER));
      dispatch(getOrCreateFeatureFlag(FeatureConstants.SIC_FileNet_Connectivity));
    }
  }, [userClaims]);

  useEffect(() => {
    if (shouldRefreshToken()) {
      const currentURL = new URLSearchParams(window.location.search);
      const state = currentURL.get('state');
      if (state && localStorage.getItem('claims')) {
        dispatch(tokenRefresh(state, dispatch));
      }
    }
  }, [window.location.search]);

  useEffect(() => {
    setExpiresAtForAutoRefresh(dispatch);
    startWindowMessageListener(dispatch, history);
    pipe(
      O.some(loadClaimsAndProfile(dispatch)),
      O.ap(getUserClaims()),
      O.fold(
        () => {},
        load => load()
      )
    );

    loadAdobeAnalyticsHeader(footerFeatureVariables.ENV, setAnalyticsScriptLoaded);
    loadWhatFixHeader();
    // loadSisenseScriptHeader(footerFeatureVariables.ENV, setSisenseScriptLoaded);
    dispatch(updatefeatureVariables({...footerFeatureVariables}));
  }, []);

  useEffect(() => {
    function delay(ms: number) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }

    async function showReviewSecurityQuestionsAndResetPassword() {
      await delay(2000);
      const updatedClaims = String(fromLocalStorage(LocalStorageKey.Claims)());
      if (updatedClaims) {
        const jsonClaims = JSON.parse(updatedClaims);
        if (
          jsonClaims &&
          jsonClaims.shouldResetPassword &&
          jsonClaims.shouldResetPassword === true &&
          isLoaded(userClaims) &&
          !sessionStorage.getItem('wasPasswordUpdated')
        ) {
          dispatch(showModal(ModalState.ReviewSecurityQuestionsAndResetPassword(userClaims.Data)));
        }
      }
    }

    showReviewSecurityQuestionsAndResetPassword();
  }, [userClaims]);

  return (
    <ErrorBoundary>
      {analyticsScriptLoaded && <Analytics />}
      {isShowModal && (
        <Modal className="redirect_modal" title="Update your bookmark" open={true} hideCloseButton={true} maxWidth="sm">
          <DialogContent>
            <DialogContentText id="alert-dialog-description" style={{color: '#505054'}}>
              <div className="logo_redirection_modal">
                <img
                  src={StandardLogo}
                  id="collapsed-menu-logo"
                  className="collapsed-menu-bar__main-logo"
                  alt="Standard Logo"
                />
              </div>
              <Typography variant="caption" component="p" className="content">
              You are in the right place. MyAccess is now part of The Standard. Same website - New look and address.
              Please be sure to update your bookmark!
              <p style={{textAlign: "center"}}><strong>{urlText}</strong></p>
              You will be redirected in {secondsText}.
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{justifyContent: 'center', paddingBottom: '30px'}}>
            <Button className="redirect_modal__btn" color="primary" variant="contained" onClick={() => window.location.replace(toURL)}>
              Continue to Broker Portal app
            </Button>
          </DialogActions>
        </Modal>
      )}
      <Switch>
        <Route exact path="/login" render={() => <Login />} />
        <Route exact path="/noAccess" render={() => <UserAccountNotFound />} />
        <Route
          exact
          path={UserRouteConstants.SIGN_UP}
          // @ts-ignore
          render={routeProps => <IndependentAgentEntry {...routeProps} />}
        />
        <Route exact path={UserRouteConstants.USERNAME_RECOVERY} render={() => <UsernameRecovery />} />
        <Route exact path={UserRouteConstants.PASSWORD_RECOVERY} render={() => <PassRecovery />} />
        <Route
          exact
          path={UserRouteConstants.PASSWORD_RECOVERY_SHOW_SECURITY_QUES}
          render={() => <PassRecoverySecurityQues />}
        />
        <Route
          exact
          path={UserRouteConstants.PASSWORD_RECOVERY_EMAIL_SENT}
          render={() => <PassRecoveryEmailSent />}
        />
        <Route exact path={UserRouteConstants.PASSWORD_RESET_REQUEST_EXPIRED} render={() => <PasswordResetExpired />} />
        <Route exact path={UserRouteConstants.PASSWORD_RESET_FORM(':requestKey')} render={() => <PasswordReset />} />
        <Route exact path={UserRouteConstants.REGISTRATION} render={() => <IndependentAgentRegistration />} />
        <Route
          exact
          path={UserRouteConstants.EXTERNAL_USER_SIGN_UP(':preregistrationKey')}
          render={() => <IndependentAgentSignUp />}
        />
        <Route exact path="/legal-notices/:id" render={() => <LegalNoticePage />} />
        <AuthorizedRoutes>
          <WithClaims>
            {claims => (
              <TermsAndConditions claims={claims}>
                <Menu userClaims={fromResult(claims)} />
                {!isMobileView && <NavigationMenu userClaims={fromResult(claims)} />}
                <div className="app">
                  {!isMobileView && <BreadcrumbsBar />}
                  <AgentNewsAdministrationRoutes />
                  <AgencyAdministrationRoutes />
                  <CustomerSupportRoutes />
                  <UserRoutes />
                  <LegalDocumentationRoutes url={location.pathname} />
                  <CoreRoutes claims={claims} />
                  <AgentRoutes claims={claims} />
                  <QuotingRoutes claims={claims} />
                  <DocumentRoutes />
                </div>
                <PortalFooterWrapper />
              </TermsAndConditions>
            )}
          </WithClaims>
        </AuthorizedRoutes>
      </Switch>
      <ModalConductor />
      <Notifications />
    </ErrorBoundary>
  );
};

export default WithTheme(App);
